import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { CalendarDays, Paperclip, Pin, SendHorizontal, X } from "lucide-react";
import {
  AcDialog,
  BackButton,
  Button,
  EmptyState,
  Icon,
  IconButton,
  Spinner,
  useCoarsePointer,
  useMinWidth,
  useUnsavedEdits,
} from "@cntrl/ui";
import { holdHaptic, isNative } from "@cntrl/native";
import { useMe } from "../../app/hooks/useSession.js";
import {
  useThread,
  useSendMessage,
  useAddReaction,
  useRemoveReaction,
  useDeleteMessage,
  useMarkThreadRead,
  useStartConversation,
  type MessageDto,
  useLeaveThread,
  useSendAttachment,
  usePinMessage,
  useUnpinMessage,
} from "../../app/hooks/useInbox.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import {
  directCounterpartIds,
  formatMessageTime,
  groupMessages,
  nameInitials,
  threadTags,
  showThreadDate,
  threadTitle,
  formatFileSize,
} from "../../app/hooks/inboxFormat.js";
import { useMediaObjectUrl } from "../../app/hooks/useMedia.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { MessageActions } from "./MessageActions.js";
import { Holdable, MessageActionsSheet } from "./MessageActionsSheet.js";

/** The reaction a double-tap adds (ART-264) — one of REACTION_EMOJIS, so it is a chip like any other. */
const HEART = "❤️";
import { MessageAttachment } from "./MessageAttachment.js";
import { ATTACHMENT_ACCEPT, attachmentRefusal, compressImage } from "./attachmentFile.js";
import { EditChatModal, derivedChatName } from "./EditChatModal.js";
import { GroupMembersModal } from "./GroupMembersModal.js";
import { CrewMark } from "./CrewMark.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { pushToast } from "../../app/toasts.js";
import { openPersonProfile } from "../../app/profilePanel.js";
import type { ShowLinkResolver } from "../../app/showLink.js";
import styles from "./Inbox.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

/** A person a DRAFT conversation is addressed to — nothing exists server-side until the first message. */
export interface DraftPerson {
  personId: string;
  displayName: string;
  avatarMediaId: string | null;
}

export interface ThreadViewProps {
  /** The open thread. Omitted for a DRAFT, which has no id because it has no row yet. */
  threadId?: string;
  /** A conversation being composed with somebody for the first time — see the note on drafts below. */
  draft?: DraftPerson;
  /** A draft's first message resolves to a real thread; the caller routes to it. */
  onDraftStarted?: (threadId: string) => void;
  /** Mobile: a back affordance to the thread list. Omitted on the desktop two-pane. */
  onBack?: () => void;
  /**
   * Where this chat's SHOW lives, from the shell doing the rendering (ART-159, C-Mobile) — the header's
   * Day of Show link. Undefined, or a resolver that answers undefined, means no door is drawn.
   *
   * A resolver rather than a handler because one shell sometimes has no address for a given band (see
   * `showLink.ts`), and a header must not offer a door onto nowhere.
   */
  showHref?: ShowLinkResolver;
  /**
   * Go somewhere in the app. Supplied with {@link showHref} and used only by it — the shells' existing
   * `onNavigate` idiom, the same pair `InboxButton` already takes (`route` + `onNavigate`).
   */
  onNavigate?: (route: string) => void;
}

/**
 * The right-pane conversation. Renders message bubbles with sender attribution + device-local
 * 12h timestamps (never a trailing `Z`), collapsed reaction chips + a picker (OPTIMISTIC add/remove), a
 * per-message delete on OWN messages (rendering deleted as a "message deleted" tombstone), and a composer.
 * Offers — reading AND sending — are gone from chats (ART-159, PO 2026-09-11: cut outright with the
 * band thread kind). Marks the thread read on open AND on every message that arrives while it is open.
 *
 * Context-SAFE by construction: nothing here reaches for a band, so the same component serves the artist
 * shell (a band mounted) and the roster shell (none). Anything that ever needs one goes in a child that
 * mounts only where there is one — the CrewProfileModal rule.
 * Loading / error / empty states are designed.
 *
 * **A DRAFT is a conversation with no row behind it (ART-159).** Picking somebody in "+ New" used to POST a
 * thread immediately, so changing your mind left an empty conversation in both inboxes forever. A draft
 * carries only the person; the first message creates the thread and sends, in that order, and everything
 * else on this screen is inert until then — there is nothing to mark read, nothing to react to, and no
 * history to page. Navigating away from one leaves nothing behind because there is nothing to leave.
 */
export function ThreadView({
  threadId,
  draft,
  onDraftStarted,
  onBack,
  showHref,
  onNavigate,
}: ThreadViewProps) {
  const q = useThread(threadId);
  const me = useMe();
  // Bound to an id that may be absent (a draft): every one of these is inert until the thread exists, and
  // the composer routes to `start` instead. Hooks cannot be conditional, so they take the empty string and
  // are simply never fired in draft mode.
  const send = useSendMessage(threadId ?? "");
  const start = useStartConversation();
  const addReaction = useAddReaction(threadId ?? "");
  const removeReaction = useRemoveReaction(threadId ?? "");
  const del = useDeleteMessage(threadId ?? "");
  const markRead = useMarkThreadRead();

  const [text, setText] = useState("");
  // A half-typed message is a draft: the live updater does not reload under it on a return to the app.
  useUnsavedEdits(text.trim().length > 0);
  const [editing, setEditing] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // `threadId ?? ""` — the hook is unconditional (rules of hooks) and a draft has no thread to leave, which
  // is safe because Leave is only reachable from the members dialog of a loaded GROUP thread.
  const leave = useLeaveThread(threadId ?? "");
  // C-Attach / C-Pin: bound to `threadId ?? ""` for the same reason everything above is — a DRAFT has no
  // thread to attach to or pin in, and the composer refuses a file before either can fire.
  const sendFile = useSendAttachment(threadId ?? "");
  const pin = usePinMessage(threadId ?? "");
  const unpin = useUnpinMessage(threadId ?? "");
  const [staged, setStaged] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // The head has one line for the name and one for its tags, and on a phone the crew faces are pinned to
  // the same row — so the show's DATE had nowhere to go and painted through them. Clipping stops the
  // overlap but leaves a fragment ("O" of "Oct 26"), which is worse than nothing; the date is dropped
  // outright at the narrow shape instead, which is also where the handoff puts it ("the date moved up to
  // the eyebrow"). The shared breakpoint hook, never a media query with a retyped number.
  const isWide = useMinWidth("md");
  // A message's actions (ART-264) answer TWO questions, and they come apart on a desktop window dragged narrow:
  // - WHERE they open follows the LAYOUT — the phone chrome (the same test the shell swaps on) gets the bottom
  //   sheet, the desktop gets the floating row under the bubble;
  // - HOW you open them follows the INPUT — a finger holds the bubble, a mouse presses the `…`.
  // A narrow mouse window therefore keeps the `…` and it opens the sheet; a phone has no `…` at all.
  const wideChat = !usePhoneLayout();
  const sheetActions = isNative() || !wideChat;
  const coarsePointer = useCoarsePointer();
  const holdToAct = isNative() || coarsePointer;
  const [actionsFor, setActionsFor] = useState<MessageDto | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);

  const myPersonId = me.data?.person?.personId;
  const detail = q.data;

  // Mark the thread read on open AND on every message that ARRIVES while it is open (server-confirm;
  // repaints the list badge + the shell's inbox badge).
  //
  // It used to be once per open, keyed on the thread id alone — so a message landing in the conversation you
  // were sitting in stayed unread on the server until you left and came back. The row badged the thread you
  // were looking at, and the shell's inbox count included it. Reading is what makes something read; the fact
  // that you had already been here when it arrived is not a reason to hold it unread.
  //
  // Keyed on the newest message id, not the count: a delete is a tombstone, so the count can fall without
  // anything new having arrived, and re-marking then is a write with nothing behind it. MY OWN send is
  // skipped for the same reason the server excludes own messages from the unread tally — it cannot make
  // anything unread, so it should not cost a round trip.
  const lastMessage = detail?.messages?.[detail.messages.length - 1];
  const lastInboundId =
    lastMessage && lastMessage.senderPersonId !== myPersonId ? lastMessage.messageId : undefined;
  const markedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!threadId) return;   // a draft has nothing to mark
    const stamp = `${threadId}:${lastInboundId ?? ""}`;
    if (markedRef.current === stamp) return;
    markedRef.current = stamp;
    markRead.mutate(threadId);
    // markRead is a stable mutation object; depending on it would re-run this effect on every settle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, lastInboundId]);

  // Keep the newest message in view as the conversation grows / a send lands. Optional-called so it's a no-op
  // where the platform doesn't implement it (jsdom under test).
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ block: "end" });
  }, [detail?.messages?.length]);

  /**
   * AN IMAGE THAT LOADS AFTER THE SCROLL PUSHES THE NEWEST MESSAGE BELOW THE FOLD (C-Attach).
   *
   * The effect above runs when the message COUNT changes, and at that instant an attachment's `<img>` has no
   * bytes and no height. It grows a moment later — by up to 320px — and everything under it goes off the
   * bottom, so the conversation opens part-way up with the newest message out of sight. Found by opening the
   * app: every DOM assertion passes against it, because the messages are all present and in order.
   *
   * `onLoadCapture`, because `load` does not BUBBLE — a listener on the container only sees it in the
   * capture phase. And only when the reader was already AT the bottom: somebody who has scrolled up to read
   * history must not be yanked back down because a picture finished loading.
   */
  function onMediaLoad() {
    const pane = messagesRef.current;
    if (!pane) return;
    const distanceFromBottom = pane.scrollHeight - pane.scrollTop - pane.clientHeight;
    // The image's own height is what just arrived, so "was I at the bottom" has to be measured with a
    // tolerance that admits it — anything under a bubble's worth of slack counts as still being there.
    if (distanceFromBottom <= MEDIA_RESCROLL_SLACK_PX) {
      messagesEndRef.current?.scrollIntoView?.({ block: "end" });
    }
  }

  // personId → display name, from the directory the thread read serves (IPersonDirectory owns the
  // precedence: display name → first+last → email). Before this the payload carried ids and nothing else,
  // so every message from anyone but you read the literal "Member" — on a five-person crew chat, five
  // indistinguishable rows.
  const personNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of detail?.people ?? []) if (p.personId && p.displayName) map.set(p.personId, p.displayName);
    return map;
  }, [detail]);

  // …and their faces, off the SAME directory, so the header's member stack cannot picture one person and
  // name another.
  const avatarById = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const p of detail?.people ?? []) if (p.personId) map.set(p.personId, p.avatarMediaId ?? null);
    return map;
  }, [detail]);

  // ONE authority for a thread's name, shared with the list row — threadTitle. It used to be a second,
  // shorter rule here, which is why an opened show thread was headed "Conversation" while its row said
  // "Show thread"; on a Pro band that second rule would also have dropped the PARTY, so all five of a show's
  // chats would open under one heading (ART-145).
  //
  // SPREAD the detail rather than listing fields. Sharing the function is not enough on its own: every field
  // threadTitle reads has to REACH it, and hand-listing them means a field added to the shape lands at the
  // row's call site and silently misses this one. That is exactly what happened when `show` arrived — the
  // sidebar named the venue and the header still said "Show thread · C Party", from one shared function.
  // A spread carries whatever the shape grows next.
  const nameCtx = useMemo(() => ({ names: personNameById, myPersonId }), [personNameById, myPersonId]);

  // THE DETAIL AND THE ROW NAME PEOPLE DIFFERENTLY, and threadTitle speaks the row's dialect. A summary
  // carries `participantPersonIds`; the detail carries `participants` objects — so handing the detail
  // straight over gave the DM branch an undefined list, no counterpart, and the literal "Direct message" on
  // the header of every conversation, under a sidebar row that named the person correctly. It type-checked
  // because both fields are optional on the generated shapes, which is exactly how a field that never
  // arrives looks like one that is merely absent.
  const titleSubject = useMemo(
    () =>
      detail
        ? { ...detail, participantPersonIds: (detail.participants ?? []).map((p) => p.personId as string) }
        : undefined,
    [detail],
  );
  // SERVED, never derived. The lock window is a policy row and the show's date sits behind a fence the
  // person-scoped routes never set, so the client could not compute this if it wanted to.
  const locked = !draft && detail?.acceptsMessages === false;

  const title = draft
    ? draft.displayName
    : titleSubject
      ? threadTitle(
          titleSubject,
          // The same directory the message rows are named from, so the heading and the bubbles cannot call
          // one person two things.
          nameCtx,
        )
      : "Conversation";

  // The chat's PICTURE, and the fallback chain is shared with the list row so a chat looks like itself in
  // both places: the name somebody set, else the show's flyer, else initials. A DM has neither and draws the
  // other person's face instead (below).
  // THE CHAT'S OWN PICTURE comes from the global avatar store — no band in the path, which is what lets a
  // group chat (no show, no band) have one at all, and what lets the other side of a CROSS-BAND chat see it.
  // The show's FLYER behind it is genuinely band media and stays band-scoped: it is the show's file, not the
  // chat's identity, and everyone in a show chat reaches that band by construction.
  const chatPicture = useAvatarUrl("thread", detail?.threadId, detail?.avatarMediaId);
  const flyer = useMediaObjectUrl(
    chatPicture ? null : (detail?.show?.flyerMediaId ?? null),
    null,
    { bandId: detail?.show?.bandId, variant: "thumb" },
  );
  const markUrl = chatPicture ?? flyer.url;
  // BOTH tags, the same two the sidebar row carries (PO, 2026-09-11) — what kind of chat this is and which
  // party it is for. They are the two facts that came OUT of the name when it became just the venue.
  const tags = detail ? threadTags(detail) : { kind: null, party: null };
  const showDate = detail ? showThreadDate(detail.show) : "";
  // THE DAY OF SHOW LINK. Resolved through the shell's rule, so it is absent wherever this chat's show has
  // no address from here — which on the artist shell is any chat whose band is not the mounted one.
  const dayOfShowHref =
    detail?.show?.showId && detail.show.bandId
      ? showHref?.({ showId: detail.show.showId, bandId: detail.show.bandId, date: detail.show.date })
      : undefined;
  // Everyone in the chat, the viewer included — a group's roster is the group, and a stack that silently
  // dropped "you" would count wrong beside a name that does not.
  //
  // BOTH multi-person kinds, which it was not (PO, 2026-09-11): it read `kind === "show"` alone, so the
  // chats whose membership somebody actually decides — the ones you can add to, remove from and leave —
  // were the only ones that never pictured who was in them. A DM is excluded for the opposite reason: a
  // stack of one face beside that same person's name and picture says nothing twice.
  const memberIds = useMemo(
    () =>
      detail?.kind === "show" || detail?.kind === "group" || detail?.kind === "bus"
        ? (detail.participants ?? []).map((p) => p.personId as string)
        : [],
    [detail],
  );
  const dmPersonId =
    draft?.personId ??
    (titleSubject?.kind === "direct" ? directCounterpartIds(titleSubject, myPersonId)[0] : undefined);
  const dmAvatarMediaId =
    draft?.avatarMediaId ?? (dmPersonId ? (avatarById.get(dmPersonId) ?? null) : null);

  /**
   * THE iOS COMPOSER RE-PIN (C-iOS). The soft keyboard ANIMATES UP after `focus` fires, so a composer
   * scrolled into view at focus time is half-covered a moment later — the field the reader is typing in
   * ends up behind the keyboard, which is the one place it must never be.
   *
   * Three passes at 80 / 280 / 520 ms, which is the handoff's own schedule and covers the animation's
   * start, middle and settle. A single delayed call cannot: too early and the keyboard is still moving, too
   * late and the reader watches their own text scroll away and come back.
   *
   * **NATIVE ONLY.** A browser has no soft keyboard that reflows the viewport, and on desktop this would be
   * three unexplained scroll jumps every time somebody clicked the composer. It is the same seam the tab bar
   * already yields to, and the same reason `lockNativeViewport` is a deliberate web no-op.
   */
  function repinComposer() {
    if (!isNative()) return;
    for (const ms of COMPOSER_REPIN_MS) {
      window.setTimeout(() => composerRef.current?.scrollIntoView?.({ block: "end" }), ms);
    }
  }

  // AUTO-GROW. A textarea does not size to its content natively, and `rows={1}` with a fixed height means a
  // long message scrolls inside two lines. `field-sizing: content` would be the one-liner, but Safari does
  // not ship it — so the height is set from scrollHeight, reset to `auto` first because scrollHeight never
  // SHRINKS below the current height and the field would otherwise only ever grow. The ceiling is CSS
  // (`max-height` + `overflow-y: auto`), so the cap and the scroll stay one decision.
  const composerRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  // Computed once per render over the served order (oldest → newest), so every row's answer comes from one
  // pass rather than each row re-deriving what the one above it decided. It carries BOTH the run header and
  // the day separator, because both answer "what does the message above this one say".
  const grouping = useMemo(() => groupMessages(detail?.messages ?? []), [detail]);

  /**
   * MY LAST MESSAGE, and whether everyone else has read it (C-Read).
   *
   * **"Read" means EVERYONE else has**, which on a DM is the spec's two-sided receipt and on a group is the
   * only statement that stays true as the group grows. A "read by 3 of 8" counter would be more information
   * and is a design nobody has asked for; a receipt that meant "somebody read it" would be a different
   * sentence wearing the same word.
   *
   * **On MY LAST message only**, which is the spec's rule and also the only place it is useful: a column of
   * "Read" down every message you ever sent is noise, and the question a sender is actually asking is
   * whether the last thing they said landed.
   *
   * Both stamps are DB-written (`last_read_on = now()`, `messages.created_on DEFAULT now()`), so this is ONE
   * clock — the comparison this codebase has three recorded defects from getting wrong.
   */
  const readReceipt = useMemo(() => {
    const messages = detail?.messages ?? [];
    const mineLast = [...messages].reverse().find((m) => m.senderPersonId === myPersonId && !m.deleted);
    if (!mineLast?.createdOn || !myPersonId) return null;
    const others = (detail?.participants ?? []).filter((p) => p.personId !== myPersonId);
    // Nobody else in the chat — a group you are alone in, or a thread mid-reconcile. "Read" would be a
    // claim about an empty set, which is vacuously true and reads as a lie.
    if (others.length === 0) return null;
    const sentAt = new Date(mineLast.createdOn).getTime();
    const allRead = others.every(
      (p) => p.lastReadOn && new Date(p.lastReadOn).getTime() >= sentAt,
    );
    return allRead ? mineLast.messageId : null;
  }, [detail, myPersonId]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const body = text.trim();
    // A STAGED FILE is a message on its own, so this is the one path where an empty composer still sends.
    if (!body && !staged) return;
    if (draft) {
      // The two calls a first message costs — create (get-or-create) then send. The caller routes to the
      // thread the server resolved, which is the EXISTING conversation when there already was one.
      start.mutate(
        { personId: draft.personId, body },
        { onSuccess: (id) => { setText(""); onDraftStarted?.(id); } },
      );
      return;
    }
    if (staged) {
      // The TEXT rides with the file rather than sending as a second message: one send, one bubble, which
      // is what the sender typed. Both clear together on success for the same reason — a composer that kept
      // the caption after the photo went would send it again.
      sendFile.mutate(
        { file: staged, text: body },
        { onSuccess: () => { setText(""); setStaged(null); } },
      );
      return;
    }
    send.mutate(body, { onSuccess: () => setText("") });
  }

  /**
   * Stage a file the composer will send. Refused HERE, before any upload, so somebody on a hotel wifi is
   * told in the moment rather than after pushing 40 MB — and compressed after the refusal, so an oversized
   * photo is not decoded just to be rejected.
   *
   * The input is RESET either way: picking the same file twice in a row fires no `change` event otherwise,
   * so a mis-pick you cancelled could not be re-picked.
   */
  async function onPickFile(file: File | undefined) {
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    const refusal = attachmentRefusal(file);
    if (refusal) {
      pushToast(refusal);
      return;
    }
    setStaged(await compressImage(file));
  }

  /** A pin is a toggle from the message's own menu: pinning the pinned one clears it. */
  function togglePin(messageId: string) {
    if (detail?.pinnedMessage?.messageId === messageId) unpin.mutate();
    else pin.mutate(messageId);
  }


  function senderLabel(m: MessageDto): string {
    if (m.senderPersonId && m.senderPersonId === myPersonId) return "You";
    if (m.senderPersonId && personNameById.has(m.senderPersonId)) {
      return personNameById.get(m.senderPersonId) as string;
    }
    // Still reachable: a sender the directory could not resolve (a deleted person). "Member" is the designed
    // neutral rather than a raw uuid.
    return "Member";
  }

  function toggleReaction(m: MessageDto, emoji: string) {
    const mine = (m.reactions ?? []).some((r) => r.emoji === emoji && r.mine);
    if (mine) removeReaction.mutate({ messageId: m.messageId as string, emoji });
    else addReaction.mutate({ messageId: m.messageId as string, emoji });
  }

  return (
    <div className={styles.chatPane}>
      {/* THE CHAT HEAD (ART-159, reshaped by the PO 2026-09-11). THREE header BUTTONS became TWO HOVER
          REGIONS, and the rule behind the change is that a header is a set of facts you can press rather
          than a toolbar: the SUBJECT (picture + name + what kind of chat it is) is the Edit door, and the
          CREW is the Members door. Each one opens what it is a picture of, which is why neither needs a
          verb on it — "Edit chat", "Members" and "Leave" were three words competing with the chat's own
          name for the only line it has.

          The tags moved DOWN, into the line the crew used to hold: a pill on the name line sits inside the
          Edit hit area and reads as a control within a control. Leaving moved INTO the Members dialog,
          onto your own row — the one place the act is already described (it is the row you would remove if
          it were somebody else's). */}
      {/* `data-close` reserves the corner the panel's ✕ floats over — and ONLY where that ✕ exists. It is
          drawn on desktop and withheld on the mobile takeover (which has its own back arrow), so `isWide`
          is exactly the condition: a head is only ever rendered with a thread open. */}
      <div className={styles.chatHeader} data-close={isWide || undefined}>
        {onBack && <BackButton onClick={onBack} label="Back to messages" />}

        <div className={styles.headSubject}>
          {/* THE WHOLE RECTANGLE IS THE CONTROL, and it is a stretched overlay rather than a wrapper button
              because the chat's name is an <h3>: a heading is flow content, so nesting it inside a button
              is invalid HTML and — worse — a button's children are PRESENTATIONAL to a screen reader, which
              would delete the heading outright. The overlay keeps the heading, gives the region one clean
              accessible name, and the hover fill paints behind the text (see `.headHit`).

              It renders only on the SERVED `canEdit` verdict, so the hover affordance and the route's
              audience are one answer — a DM has no name of its own to edit and correctly gets no door. */}
          {detail?.canEdit && (
            <button
              type="button"
              className={styles.headHit}
              aria-label="Edit chat"
              onClick={() => setEditing(true)}
            />
          )}

          {dmPersonId ? (
            <PersonAvatar
              personId={dmPersonId}
              avatarMediaId={dmAvatarMediaId}
              initials={nameInitials(title)}
              size="md"
            />
          ) : detail?.kind === "bus" && !chatPicture ? (
            // A BUS chat with no picture (ART-304): the people on the coach, the same mark its list row draws.
            <CrewMark
              personIds={memberIds.filter((id) => id !== myPersonId)}
              avatars={avatarById}
              names={personNameById}
              fallback={GROUP_MARK}
              shape="rounded"
            />
          ) : (
            <span className={styles.chatMark} aria-hidden="true">
              {markUrl ? <img className={styles.chatMarkImg} src={markUrl} alt="" /> : GROUP_MARK}
            </span>
          )}

          <div className={styles.chatHeading}>
            <h3 className={styles.chatTitle}>{q.isLoading && !draft ? "Conversation" : title}</h3>
            {/* The sub-line: what kind of chat this is, which party it is for, and the show's date — the
                three facts that came OUT of the name when it became just the venue. The pills live HERE and
                not on the list row, which groups into Shows and Direct and would repeat its own heading. */}
            {(tags.kind || tags.party || showDate) && (
              <div className={styles.chatSub}>
                {tags.kind && (
                  <span className={styles.tag} data-tone={tags.kindTone}>
                    {tags.kind}
                  </span>
                )}
                {tags.party && (
                  <span className={styles.tag} data-tone="party">
                    {tags.party}
                  </span>
                )}
                {showDate && isWide && <span className={styles.headerDate}>{showDate}</span>}
              </div>
            )}
          </div>
        </div>

        {/* THE CREW, ON EVERY GROUP CHAT — it used to render on show chats alone, so the chats whose roster
            a person actually decides were the ones that never pictured it.

            It is a real button rather than a stretched overlay because everything in it is phrasing content
            (faces and one line of text), and it is offered to EVERY participant: who is in the room is a
            fact about the chat, where changing it is the gated act — so the dialog it opens narrows its own
            controls on `canManageMembers` rather than the door being withheld. The faces are decorative on
            their own, so the ROSTER is named in text, and that text is also the button's accessible name. */}
        {/* DAY OF SHOW — the spec's "header's fourth column", which in our head is the trailing cluster the
            crew already sits in. It goes BEFORE the faces: this is a destination and they are a dialog, so
            the thing that leaves the screen reads first.

            Icon‑only under `md`. At 390px the head is already carrying a name, two tags and five faces —
            which is what made the show's DATE collide there — so the word is the first thing to give. */}
        {dayOfShowHref && (
          <a
            className={styles.headShow}
            href={dayOfShowHref}
            onClick={(e) => {
              // Client-side, like every other in-app link: a full page load would drop the overlay AND the
              // page underneath it. The href is real so the row is a proper link — middle-click and
              // "open in new tab" keep working, which a button would take away.
              e.preventDefault();
              onNavigate?.(dayOfShowHref);
            }}
          >
            <Icon icon={CalendarDays} size={16} />
            {isWide && <span className={styles.headShowLabel}>Day of show</span>}
            {!isWide && <span className={styles.srOnly}>Day of show</span>}
          </a>
        )}

        {memberIds.length > 0 && (
          <button type="button" className={styles.headMembers} onClick={() => setMembersOpen(true)}>
            {memberIds.slice(0, MEMBERS_SHOWN).map((id) => (
              <PersonAvatar
                key={id}
                personId={id}
                avatarMediaId={avatarById.get(id) ?? null}
                initials={nameInitials(personNameById.get(id) ?? "")}
                size="xs"
                className={styles.memberFace}
              />
            ))}
            {memberIds.length > MEMBERS_SHOWN && (
              <span className={styles.memberMore} aria-hidden="true">
                +{memberIds.length - MEMBERS_SHOWN}
              </span>
            )}
            <span className={styles.srOnly}>
              Members: {memberIds.map((id) => personNameById.get(id) ?? "Member").join(", ")}
            </span>
          </button>
        )}
      </div>

      {/* THE PINNED BANNER (C-Pin). It sits BETWEEN the head and the transcript rather than inside the
          scroller, because a pin that scrolls away is a pin you have to go and find — which is the thing it
          exists to stop. The whole row is the "jump to it" control and the ✕ is the unpin, so the two acts
          are separated by position rather than by a menu: pressing the message takes you TO it, pressing
          the cross takes it DOWN.

          Offered to everybody, because pinning is any participant's — the same reason the message menu
          shows Pin on every message and Delete only on your own. */}
      {detail?.pinnedMessage && (
        <div className={styles.pinBanner}>
          <span className={styles.pinIcon} aria-hidden="true">
            <Icon icon={Pin} size={16} />
          </span>
          <button
            type="button"
            className={styles.pinBody}
            onClick={() => {
              const el = document.getElementById(`msg-${detail.pinnedMessage?.messageId}`);
              el?.scrollIntoView?.({ block: "center", behavior: "smooth" });
            }}
          >
            <span className={styles.pinLabel}>
              Pinned by {senderLabel(detail.pinnedMessage)}
            </span>
            <span className={styles.pinText}>
              {/* A pinned PHOTO has no words, so the banner says what it is rather than rendering blank —
                  the same classification the inbox row composes, from the same served key. */}
              {detail.pinnedMessage.body?.trim() ||
                (detail.pinnedMessage.attachment?.kind === "image"
                  ? "📷 Photo"
                  : detail.pinnedMessage.attachment
                    ? `📎 ${detail.pinnedMessage.attachment.fileName ?? "File"}`
                    : "")}
            </span>
          </button>
          <button
            type="button"
            className={styles.pinClose}
            aria-label="Unpin this message"
            onClick={() => unpin.mutate()}
          >
            <Icon icon={X} size={16} />
          </button>
        </div>
      )}

      {q.isLoading && !draft ? (
        <div className={styles.stateCenter} role="status" aria-live="polite">
          <Spinner size={22} label="Loading conversation…" />
        </div>
      ) : q.isError && !draft ? (
        <div className={styles.stateCenter}>
          <EmptyState
            title="Couldn't load this conversation"
            description={errorMessage(q.error, "Please try again.")}
            action={<Button onClick={() => void q.refetch()}>Retry</Button>}
          />
        </div>
      ) : (
        <>
          {/* A TRANSCRIPT IS A LOG. The role names the region (so a test and a screen reader can both address
              "the conversation" rather than the whole pane) and makes new messages announce themselves as
              they arrive, which is what a live chat owes a reader who is not watching it. */}
          <div
            ref={messagesRef}
            className={styles.messages}
            role="log"
            aria-label="Conversation"
            onLoadCapture={onMediaLoad}
          >
            {(detail?.messages ?? []).length === 0 ? (
              <EmptyState
                title={draft ? `Start a conversation with ${draft.displayName}` : "No messages yet"}
                // A DRAFT gets a line because it is a state with a CONSEQUENCE: nothing is created, and
                // nobody is told anything, until a message is actually sent. An empty conversation does not
                // — the composer underneath is the whole instruction.
                description={
                  draft ? "Nothing is sent until you write something — leave now and this goes away." : undefined
                }
              />
            ) : (
              (detail?.messages ?? []).map((m, i) => {
                const mine = !!m.senderPersonId && m.senderPersonId === myPersonId;
                // A run of messages from one sender wears ONE header, until ten minutes have passed since the
                // run began — groupMessages owns that rule, over the served (reading) order.
                const showHeader = grouping[i]?.showHeader ?? true;
                const daySeparator = grouping[i]?.daySeparator ?? null;
                // A SYSTEM NOTE IS NOT FROM ANYBODY (C-Day) — "Dave added Sam", written by the membership
                // change itself. Centred and unbubbled, with no sender header, no time, no reactions and no
                // actions: every one of those would say a person is talking. It still gets its day
                // separator, because it is still something that happened on a day.
                if (m.kind === "system") {
                  return (
                    <div key={m.messageId ?? `m-${i}`} className={styles.systemRow}>
                      {daySeparator && (
                        <div className={styles.daySeparator} role="separator" aria-label={daySeparator}>
                          <span className={styles.daySeparatorLabel}>{daySeparator}</span>
                        </div>
                      )}
                      <span className={styles.systemNote}>{m.body}</span>
                    </div>
                  );
                }
                return (
                  <Fragment key={m.messageId ?? `m-${i}`}>
                    {/* THE DAY SEPARATOR (C-Day). A `<div role="separator">` with the day as its accessible
                        name, not a bare line: a rule with no text is decoration, and the date is the whole
                        content. It is a SIBLING of the row rather than a child of it, so it spans the full
                        pane and its label sits centred: `.msgRow` is capped at 78% and end-aligned for my own
                        messages, so a separator nested inside one rendered as a short rule shoved to whichever
                        side that day's first message happened to be on. The shared `key` on the Fragment keeps
                        it adjacent to its own day when the list re-renders. */}
                    {daySeparator && (
                      <div className={styles.daySeparator} role="separator" aria-label={daySeparator}>
                        <span className={styles.daySeparatorLabel}>{daySeparator}</span>
                      </div>
                    )}
                  <div
                    // The id is the PIN's jump target. It rides the row rather than the bubble so a
                    // scroll-into-view centres the sender header with it rather than orphaning it above.
                    id={m.messageId ? `msg-${m.messageId}` : undefined}
                    className={mine ? `${styles.msgRow} ${styles.msgMine}` : styles.msgRow}
                    data-grouped={!showHeader || undefined}
                  >
                    {showHeader && (
                      <div className={styles.msgMeta}>
                        {/* WHO IS TALKING is a small face + the name, and pressing either opens their profile
                            over the chat (ART-264, PO: "dont need peoples roles"). Your own messages stay a plain
                            "You", and a sender the directory could not resolve (a deleted person) is not a door. */}
                        {!mine && m.senderPersonId && personNameById.has(m.senderPersonId) ? (
                          <button
                            type="button"
                            className={styles.senderButton}
                            aria-label={`View ${senderLabel(m)}'s profile`}
                            onClick={() => openPersonProfile(m.senderPersonId as string)}
                          >
                            <PersonAvatar
                              personId={m.senderPersonId}
                              avatarMediaId={avatarById.get(m.senderPersonId) ?? null}
                              initials={nameInitials(senderLabel(m))}
                              size="xs"
                            />
                            <span className={styles.sender}>{senderLabel(m)}</span>
                          </button>
                        ) : (
                          <span className={styles.sender}>{senderLabel(m)}</span>
                        )}
                        <span className={styles.time}>{formatMessageTime(m.createdOn)}</span>
                      </div>
                    )}

                    <div className={styles.bubbleRow}>
                      {m.deleted ? (
                        <span className={styles.tombstone}>message deleted</span>
                      ) : (
                        <Holdable
                          enabled={holdToAct}
                          onHold={() => setActionsFor(m)}
                          // DOUBLE-TAP TO HEART (ART-264, PO). Adds yours and never takes it away — the
                          // Instagram gesture, where a second double-tap does not un-like; the chip under the
                          // bubble is how a heart comes off. A photo's first tap opens it full screen, so a
                          // double-tap on a picture-only message opens the photo rather than hearting it.
                          onDoubleTap={() => {
                            if (!(m.reactions ?? []).some((r) => r.emoji === HEART && r.mine)) {
                              holdHaptic();
                              toggleReaction(m, HEART);
                            }
                          }}
                          className={mine ? `${styles.bubble} ${styles.bubbleMine}` : styles.bubble}
                          // AN IMAGE IS THE BUBBLE, NOT A PICTURE INSIDE A FRAME (C-Attach). The bubble's
                          // padding + fill around a posted image read as a border nobody asked for, so an
                          // image bubble drops the frame: "only" (no caption) becomes the picture edge-to-edge,
                          // "captioned" bleeds it to the bubble's top and sides with the words below. A FILE
                          // chip keeps the frame — it is a card, not a photo — so this keys on the image kind,
                          // which the row knows before the bytes resolve.
                          data-media={
                            m.attachment?.kind === "image" ? (m.body ? "captioned" : "only") : undefined
                          }
                        >
                          {/* THE FILE ABOVE THE WORDS (C-Attach), because the words are usually a caption
                              for it. An attachment-only message renders the file alone — the server stores
                              a NULL body rather than an empty string, so there is nothing to leave a gap. */}
                          {m.attachment && threadId && (
                            <MessageAttachment threadId={threadId} attachment={m.attachment} />
                          )}
                          {m.body}
                        </Holdable>
                      )}

                      {!m.deleted && !holdToAct && (
                        <MessageActions
                          mine={mine}
                          onReact={(emoji) => toggleReaction(m, emoji)}
                          onDelete={mine ? () => del.mutate(m.messageId as string) : undefined}
                          // ANY participant may pin, so this is offered on every message — where Delete is
                          // yours alone. A draft has no thread to pin in.
                          onPin={threadId && m.messageId ? () => togglePin(m.messageId as string) : undefined}
                          pinned={!!m.messageId && detail?.pinnedMessage?.messageId === m.messageId}
                          onOpen={sheetActions ? () => setActionsFor(m) : undefined}
                        />
                      )}
                    </div>

                    {/* READ, under my last message only (C-Read) — and only once EVERYONE else has read it,
                        which is the one statement that stays true as a group grows. */}
                    {m.messageId === readReceipt && (
                      <span className={styles.readReceipt}>Read</span>
                    )}

                    {!m.deleted && (m.reactions ?? []).length > 0 && (
                      <div className={styles.reactionChips}>
                        {(m.reactions ?? []).map((r) => (
                          <button
                            key={r.emoji}
                            type="button"
                            className={r.mine ? `${styles.chip} ${styles.chipMine}` : styles.chip}
                            aria-label={`${r.mine ? "Remove" : "Add"} ${r.emoji} reaction`}
                            onClick={() => toggleReaction(m, r.emoji as string)}
                          >
                            <span>{r.emoji}</span>
                            <span>{r.count}</span>
                          </button>
                        ))}
                      </div>
                    )}

                  </div>
                  </Fragment>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* A CLOSED CHAT GETS A NOTE, NOT A DISABLED COMPOSER (ART-159 teardown). Seven days after the
              show its crew chat stops accepting messages and the history stays; the server answers 423 on a
              send, and `acceptsMessages` is the SAME verdict served on the read so this never draws a box
              over a refusal. A disabled textarea would say "you may not", which is wrong twice — it is
              nobody's permission that ran out, and there is nothing anyone can do to get it back. Drafting
              is UNREACHABLE rather than discouraged, which is what makes the sentence honest.

              `!== false` and not a truthiness test: a draft carries no detail and an older payload carries
              no field, and both of those mean "write away" rather than "closed". */}
          {locked ? (
            <p className={styles.lockedNote} role="status">
              This chat is closed — the show has passed.
            </p>
          ) : (
          <form className={styles.composer} onSubmit={onSubmit}>
            {/* THE STAGED FILE, ABOVE the box (C-Attach). Above rather than inside it, because a chip in the
                same row as the textarea is the grid the handoff records legacy breaking — "the chat-media
                patch added a third child without updating the track list, so send dropped to row 2". Here
                the row it sits in is its own, so a long filename cannot push anything anywhere. */}
            {staged && (
              <div className={styles.stagedFile}>
                <span className={styles.stagedName}>{staged.name}</span>
                <span className={styles.stagedSize}>{formatFileSize(staged.size)}</span>
                <button
                  type="button"
                  className={styles.stagedRemove}
                  aria-label={`Remove ${staged.name}`}
                  onClick={() => setStaged(null)}
                >
                  <Icon icon={X} size={16} />
                </button>
              </div>
            )}
            <div className={styles.composerBox}>
              {/* A DRAFT cannot carry a file: there is no thread to attach it to until the first message
                  creates one, and a two-step "create then upload" is exactly the staged-orphan shape the
                  send route was built to avoid. The control is ABSENT rather than disabled — a disabled
                  button says "not yet" about something that never becomes available here. */}
              {!draft && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className={styles.srOnly}
                    accept={ATTACHMENT_ACCEPT}
                    tabIndex={-1}
                    aria-hidden="true"
                    onChange={(e) => void onPickFile(e.target.files?.[0])}
                  />
                  <IconButton
                    icon={Paperclip}
                    label="Attach a file"
                    type="button"
                    size="sm"
                    shape="square"
                    // ONE per send, structurally on the server too — so the control is disabled while a file
                    // is staged rather than silently replacing it.
                    disabled={!!staged}
                    onClick={() => fileInputRef.current?.click()}
                    className={styles.composerAttach}
                  />
                </>
              )}
              <textarea
                ref={composerRef}
                className={styles.composerInput}
                rows={1}
                // The handoff's 2,000, against the server's 4,000 (ART-159). Two numbers on purpose: this one
                // is what a person can type, and the server's is the fence a crafted request meets — so a
                // composer that is one release behind still cannot build a body the server refuses.
                maxLength={2000}
                placeholder="Write a message…"
                aria-label="Write a message"
                value={text}
                onFocus={repinComposer}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    onSubmit(e);
                  }
                }}
              />
              {/* Icon-only, so the word "Send" stops reserving space inside the box. `label` is required on
                  IconButton for exactly this reason — a glyph-only control still has to announce itself. */}
              <IconButton
                icon={SendHorizontal}
                label="Send message"
                type="submit"
                size="sm"
                shape="square"
                tone="accent"
                loading={send.isPending || start.isPending || sendFile.isPending}
                // A STAGED FILE is a message on its own, so the button is live with an empty composer.
                disabled={!text.trim() && !staged}
                className={styles.composerSend}
              />
            </div>
          </form>
          )}
        </>
      )}

      {editing && detail && (
        <EditChatModal
          thread={detail}
          derivedName={derivedChatName(titleSubject ?? detail, nameCtx)}
          onClose={() => setEditing(false)}
        />
      )}

      {/* A PHONE's message actions (ART-264): a press-and-hold on a bubble opens them here, the same acts the
          desktop `…` menu offers. Re-read off the live message list on every render, so a reaction or pin that
          lands while it is open does not leave it describing a stale message. */}
      {actionsFor?.messageId && (() => {
        const m = detail?.messages?.find((x) => x.messageId === actionsFor.messageId) ?? actionsFor;
        const mine = !!m.senderPersonId && m.senderPersonId === myPersonId;
        return (
          <MessageActionsSheet
            onClose={() => setActionsFor(null)}
            onReact={(emoji) => toggleReaction(m, emoji)}
            onDelete={mine ? () => del.mutate(m.messageId as string) : undefined}
            onPin={threadId ? () => togglePin(m.messageId as string) : undefined}
            pinned={detail?.pinnedMessage?.messageId === m.messageId}
            // ART-270: a phone does not select message text (see global.scss), so Copy is how words leave a
            // bubble — offered only when there are words; a picture-only message has none.
            onCopy={m.body ? () => void copyMessage(m.body as string) : undefined}
          />
        );
      })()}

      {membersOpen && detail && (
        <GroupMembersModal
          thread={detail}
          myPersonId={myPersonId}
          // LEAVING LIVES ON YOUR OWN ROW NOW, and it is handed back up rather than confirmed in place: the
          // confirm is an AcDialog and the roster is an ActionModal, so raising one inside the other would
          // nest two focus traps at the same z. The dialog closes and this one opens, which is also the
          // truer sequence — you are being asked about the chat, not about the list you were reading.
          onLeave={detail.kind === "group" ? () => { setMembersOpen(false); setLeaving(true); } : undefined}
          onClose={() => setMembersOpen(false)}
        />
      )}

      {/* LEAVING IS CONFIRMED, and renaming is not — the asymmetry is the point. A rename is visible and
          reversible by the same person in the same dialog; leaving takes the chat off your list and needs
          somebody with the membership audience to put you back, which you may not have. */}
      {leaving && detail && (
        <AcDialog
          title="Leave this chat?"
          confirmLabel="Leave"
          tone="danger"
          onCancel={() => setLeaving(false)}
          onConfirm={() => {
            leave.mutate(undefined, { onSuccess: () => onBack?.() });
            setLeaving(false);
          }}
        >
          You'll stop receiving messages here. Someone still in the chat can add you back.
        </AcDialog>
      )}
    </div>
  );
}

/** How many faces the header shows before it counts the rest. */
const MEMBERS_SHOWN = 5;

/**
 * When the composer re-scrolls itself into view after focus, in ms (C-iOS) — the handoff's own 80/280/520.
 * Three passes rather than one because the keyboard ANIMATES: a single delayed call is either too early to
 * see the final viewport or late enough that the reader watches their field leave and come back.
 */
const COMPOSER_REPIN_MS = [80, 280, 520] as const;

/**
 * How far from the bottom still counts as "at the bottom" when an image finishes loading. It has to be at
 * least the height an image can add (`.attachImage`'s 320px cap) plus a bubble's padding, or the very
 * arrival of the picture is what disqualifies the re-scroll it exists for.
 */
const MEDIA_RESCROLL_SLACK_PX = 400;

/**
 * The picture-less group mark — the reference's own, and initials were the wrong instinct here.
 *
 * A group chat's name is not a name with initials in it: "Bluebird [test data] · Oct 1 · A Party" reduces to
 * **"B["**, because the second word starts with a bracket. That is not a near miss, it is a glyph nobody can
 * read as anything. A person HAS initials and keeps them (a DM draws `PersonAvatar` above, not this); a
 * venue-plus-date-plus-party does not, and the honest mark says "this is a group" instead of pretending.
 */
const GROUP_MARK = "#";

/** Copy a message's words to the clipboard from the phone's actions sheet (ART-270), and say whether it worked —
 *  a clipboard write can be refused (no permission, an insecure origin), and a silent failure reads as success. */
async function copyMessage(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    pushToast("Copied", "success");
  } catch {
    pushToast("Couldn't copy that message");
  }
}
